import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Phone, Mail, Clock, CheckCircle2, Loader2 } from 'lucide-react';
import { useData } from '@happyspace/sdk/data';

const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0 } };

const contactInfo = [
  { icon: MapPin, title: 'Location', value: 'Plano, Texas · DFW Area', sub: 'Serving the entire DFW community' },
  { icon: Phone, title: 'Phone', value: '[Phone coming soon]', sub: 'Call or text us anytime' },
  { icon: Mail, title: 'Email', value: '[Email coming soon]', sub: 'We reply within 24 hours' },
  { icon: Clock, title: 'Hours', value: 'Mon – Sun: 11am – 10pm', sub: 'Open every day' },
];

export default function ContactPage() {
  const { insert, loading: submitting } = useData('contact_submissions');
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', subject: '', message: '' });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await insert({ ...form, source: 'contact-page', submittedAt: new Date().toISOString() });
      setSubmitted(true);
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div className="pt-20">
      {/* Header */}
      <section className="bg-[#0A0F1E] text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            transition={{ duration: 0.6 }}
          >
            <span className="text-[#D4AF37] text-sm font-bold tracking-widest uppercase">Get in Touch</span>
            <h1 className="text-5xl font-bold mt-3 mb-4">Contact Us</h1>
            <p className="text-gray-400 text-lg">We'd love to hear from you — reservations, catering, or just saying hello!</p>
          </motion.div>
        </div>
      </section>

      {/* Info Cards */}
      <section className="py-16 bg-[#F9F6F0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {contactInfo.map((info, i) => (
              <motion.div
                key={info.title}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.5 }}
                className="bg-white rounded-3xl p-6 border border-gray-100 shadow-sm"
              >
                <div className="w-12 h-12 bg-[#0054e9]/10 rounded-2xl flex items-center justify-center mb-4">
                  <info.icon className="w-6 h-6 text-[#0054e9]" />
                </div>
                <div className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-1">{info.title}</div>
                <div className="font-bold text-gray-900 mb-1">{info.value}</div>
                <div className="text-sm text-gray-500">{info.sub}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Form + Map */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Form */}
            <motion.div
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Send a Message</h2>
              {submitted ? (
                <div className="bg-green-50 border border-green-200 rounded-3xl p-10 text-center">
                  <CheckCircle2 className="w-14 h-14 text-green-500 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Message Received!</h3>
                  <p className="text-gray-600">Thank you, {form.name}. We'll be in touch soon.</p>
                  <button
                    onClick={() => { setSubmitted(false); setForm({ name: '', email: '', phone: '', subject: '', message: '' }); }}
                    className="mt-6 text-[#0054e9] font-semibold"
                  >
                    Send another message
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-2">Name *</label>
                      <input
                        required
                        value={form.name}
                        onChange={e => setForm({ ...form, name: e.target.value })}
                        className="w-full p-4 bg-gray-50 rounded-xl focus:ring-2 focus:ring-[#0054e9] outline-none"
                        placeholder="Your full name"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-2">Phone</label>
                      <input
                        type="tel"
                        value={form.phone}
                        onChange={e => setForm({ ...form, phone: e.target.value })}
                        className="w-full p-4 bg-gray-50 rounded-xl focus:ring-2 focus:ring-[#0054e9] outline-none"
                        placeholder="(XXX) XXX-XXXX"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-2">Email *</label>
                    <input
                      required
                      type="email"
                      value={form.email}
                      onChange={e => setForm({ ...form, email: e.target.value })}
                      className="w-full p-4 bg-gray-50 rounded-xl focus:ring-2 focus:ring-[#0054e9] outline-none"
                      placeholder="your@email.com"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-2">Subject</label>
                    <select
                      value={form.subject}
                      onChange={e => setForm({ ...form, subject: e.target.value })}
                      className="w-full p-4 bg-gray-50 rounded-xl focus:ring-2 focus:ring-[#0054e9] outline-none"
                    >
                      <option value="">Select a topic...</option>
                      <option value="reservation">Reservation</option>
                      <option value="catering">Catering Inquiry</option>
                      <option value="feedback">Feedback</option>
                      <option value="general">General Question</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-2">Message *</label>
                    <textarea
                      required
                      rows={5}
                      value={form.message}
                      onChange={e => setForm({ ...form, message: e.target.value })}
                      className="w-full p-4 bg-gray-50 rounded-xl focus:ring-2 focus:ring-[#0054e9] outline-none resize-none"
                      placeholder="How can we help you?"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-[#0054e9] text-white py-4 rounded-full font-bold text-lg hover:bg-[#015796] transition-colors disabled:opacity-50 flex items-center justify-center gap-3"
                  >
                    {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Send Message'}
                  </button>
                </form>
              )}
            </motion.div>

            {/* Map placeholder + extra info */}
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="space-y-6"
            >
              <div className="bg-gradient-to-br from-[#0054e9]/5 to-[#D4AF37]/5 rounded-3xl h-80 flex items-center justify-center border-2 border-dashed border-[#0054e9]/20 overflow-hidden">
                <div className="text-center">
                  <MapPin className="w-12 h-12 text-[#0054e9] mx-auto mb-3" />
                  <p className="font-bold text-gray-700">Plano, Texas</p>
                  <p className="text-gray-500 text-sm mt-1">DFW Area</p>
                  <p className="text-xs text-gray-400 mt-3">[Address coming soon]</p>
                </div>
              </div>

              <div className="bg-[#0A0F1E] rounded-3xl p-8 text-white">
                <h3 className="text-xl font-bold text-[#D4AF37] mb-4">Catering & Events</h3>
                <p className="text-gray-300 leading-relaxed mb-6">
                  Planning a party, corporate event, or cultural celebration? Our catering team delivers
                  authentic Indian flavors to any occasion in the DFW area.
                </p>
                <ul className="space-y-3">
                  {['Birthday & Anniversary Parties', 'Corporate Lunches & Dinners', 'Cultural & Religious Events', 'Wedding Receptions'].map(item => (
                    <li key={item} className="flex items-center gap-3 text-gray-300 text-sm">
                      <div className="w-2 h-2 rounded-full bg-[#D4AF37]" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
